import { showApiErrorNotification, useApiMutation } from '@vrplatform/data';
import { showErrorNotification } from '@vrplatform/theme';
import { day } from '@vrplatform/utils';
import { useCallback } from 'react';
import { useAutomationHistoryDrawer } from '../../hooks';
import type { ExtractModalFormInputs } from './_types';

export const EXTRACT_ALL_KEY = 'extract_all';

export const useExtractModalMutation = ({
  extractConnectionIds,
  closeModal,
}: {
  extractConnectionIds: string[];
  closeModal: () => void;
}) => {
  const { open: openAutomationHistoryDrawer } = useAutomationHistoryDrawer();

  const { mutateAsync } = useApiMutation('post', '/connections/extract');

  const runExtraction = useCallback(
    async (data: ExtractModalFormInputs) => {
      try {
        if (!extractConnectionIds.length)
          return showErrorNotification({
            message: 'Missing connection id',
            color: 'yellow',
          });

        const isRange =
          data.dateRange?.every((i) => !!i) && data.dateRange.length === 2;

        const extractType = data.extractType || undefined;
        const isExtractAll = extractType === EXTRACT_ALL_KEY;

        const range = isRange
          ? {
              start: day(data.dateRange![0]!).yyyymmdd(),
              end: day(data.dateRange![1]!).yyyymmdd(),
            }
          : undefined;

        const results = await mutateAsync({
          body: {
            connections: extractConnectionIds.map((connectionId) => ({
              connectionId,
              type: isExtractAll ? undefined : extractType,
              forceUpdate: true,
              range,
            })),
          },
        });

        const syncIds = results.data.data.map((x) => x.syncId);

        closeModal();
        openAutomationHistoryDrawer(syncIds);
      } catch (error) {
        console.error(error);
        showApiErrorNotification({
          error,
          title: 'Failed to fetch data',
          defaultMessage:
            'Please try again and contact support if the issue persists.',
        });
      }
    },
    [extractConnectionIds, closeModal, openAutomationHistoryDrawer, mutateAsync]
  );

  return {
    runExtraction,
  };
};
